import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getPartHistory } from '../services/orders';
import { getAnalysisSimilars } from '../services/analyses';
import './PartHistoryPage.css';

/** LAKPED-16.2 — «qué sabemos ya» de la pieza, antes de montar la oferta.
 *
 *  Se busca primero por el código, que es lo único concluyente, y solo si no hay nada se miran
 *  los planos parecidos, que se marcan como lo que son: un parecido, no una coincidencia.
 */
const PASOS = ['Documento', 'Histórico', 'Oferta', 'PDF y envío'];

// En los datos que vienen de su Access, un importe o unas horas a cero significan «no consta»,
// no «cero euros»: por eso los dos casos se pintan igual, con una raya.
const fecha = (valor) => {
  if (!valor) return '—';
  const dia = new Date(valor);
  return Number.isNaN(dia.getTime()) ? '—' : dia.toLocaleDateString('es-ES');
};
const euros = (valor) => (valor ? valor.toLocaleString('es-ES', { style: 'currency', currency: 'EUR' }) : '—');
const horas = (valor) => (valor ? `${valor.toFixed(2).replace('.', ',')} h` : '—');

const PartHistoryPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const analysisHash = searchParams.get('analysis') || '';
  const codigo = searchParams.get('codigo') || '';
  const cliente = searchParams.get('cliente') || '';

  const [historico, setHistorico] = useState(null);
  const [similares, setSimilares] = useState(null);
  const [sinPlano, setSinPlano] = useState(false);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);

  const cargar = useCallback(async () => {
    setCargando(true);
    setError(null);
    try {
      setHistorico(await getPartHistory(codigo, analysisHash));
    } catch (err) {
      setError('No se ha podido consultar el histórico de la pieza.');
    } finally {
      setCargando(false);
    }
  }, [codigo, analysisHash]);

  useEffect(() => { cargar(); }, [cargar]);

  // Los parecidos solo se enseñan si ya están calculados: calcularlos aquí sería tener a Milena
  // esperando un minuto largo sin haberlo pedido.
  useEffect(() => {
    if (!analysisHash) {
      setSinPlano(true);
      setSimilares([]);
      return;
    }
    setSinPlano(false);
    getAnalysisSimilars(analysisHash)
      .then(res => setSimilares(res.cached ? (res.data?.results || []) : []))
      .catch(() => setSimilares([]));
  }, [analysisHash]);

  // El importe de referencia todavía no viaja a la oferta: el editor de hoy calcula el importe a
  // partir de los tiempos. Lo recogerá el paso 3, cuando la oferta pase a ser una línea con precio.
  // El precio no viaja: en los datos del cliente el importe es del pedido entero y casi siempre
  // viene a cero, así que deducir un precio por unidad sería inventarlo. Lo que sí viaja es de
  // dónde mirar, para que el precio que escriba Milena quede justificado en la oferta.
  const aLaOferta = () => {
    const query = new URLSearchParams({ analysis: analysisHash });
    if (cliente) query.set('cliente', cliente);
    if (codigo) query.set('pieza', codigo);
    const ultimo = pedidos[0];
    if (ultimo) {
      const partes = [`último pedido ${fecha(ultimo.fecha)}`];
      if (ultimo.cliente) partes.push(ultimo.cliente);
      if (ultimo.cantidad) partes.push(`${ultimo.cantidad} uds`);
      query.set('origen', partes.join(' · '));
    }
    navigate(`/offers/new?${query.toString()}`);
  };

  const resumen = historico?.resumen || {};
  const pedidos = historico?.pedidos || [];
  const ofertas = historico?.ofertas || [];
  const planos = historico?.planos || [];
  const hayHistorico = Boolean(historico?.encontrado);

  return (
    <div className="part-history">
      <ol className="part-history-pasos">
        {PASOS.map((titulo, i) => (
          <li key={titulo} className={i === 1 ? 'actual' : (i === 0 ? 'hecho' : '')}>
            <span className="part-history-paso-num">{i === 0 ? '✓' : i + 1}</span>
            {titulo}
          </li>
        ))}
      </ol>

      <h1>{cliente ? `Oferta para ${cliente} · qué sabemos ya` : 'Qué sabemos ya de esta pieza'}</h1>
      <p className="part-history-sub">
        Se busca primero por el código de la pieza y, si no hay nada, por plano parecido.
      </p>

      {cargando && <p className="part-history-cargando">Buscando en pedidos, ofertas y planos…</p>}
      {error && <p className="part-history-error">{error}</p>}

      {!cargando && historico && (
        <div className="part-history-pieza">
          <div className="part-history-cabecera">
            <div>
              <strong>{codigo || 'Pieza sin código'}</strong>
              {historico.usable === false && (
                <span className="part-history-aviso">
                  {codigo
                    ? ' · este código es demasiado corto para buscar con él'
                    : ' · el documento no trae código de pieza, así que no hay por dónde buscar'}
                </span>
              )}
            </div>
            <div className="part-history-chips">
              {hayHistorico ? (
                <>
                  <span className="part-history-chip verde">
                    Pedida {resumen.veces_pedida} {resumen.veces_pedida === 1 ? 'vez' : 'veces'}
                  </span>
                  {resumen.horas_apuntadas > 0 && (
                    <span className="part-history-chip">{horas(resumen.horas_apuntadas)} apuntadas</span>
                  )}
                  {historico.total_planos > 0 && (
                    <span className="part-history-chip">{historico.total_planos} planos con este código</span>
                  )}
                </>
              ) : (
                <span className="part-history-chip ambar">Sin histórico con este código</span>
              )}
            </div>
          </div>

          {pedidos.length > 0 && (
            <div className="part-history-cuerpo">
              <table className="part-history-tabla">
                <thead>
                  <tr>
                    <th>Fecha</th>
                    <th>Cliente</th>
                    <th className="der">Uds</th>
                    <th className="der">Horas</th>
                    <th className="der">Importe</th>
                    <th>Pedido</th>
                  </tr>
                </thead>
                <tbody>
                  {pedidos.slice(0, 10).map(p => (
                    <tr key={p.hash}>
                      <td>{fecha(p.fecha)}</td>
                      <td>{p.cliente || '—'}</td>
                      <td className="der">{p.cantidad ?? '—'}</td>
                      <td className="der">{horas(p.horas)}</td>
                      <td className="der">{euros(p.importe)}</td>
                      <td>
                        <button type="button" onClick={() => navigate(`/orders/${p.hash}`)}>
                          {p.referencia_cliente || 'ver'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {historico.total_pedidos > 10 && (
                <p className="part-history-pie">
                  Se enseñan los 10 más recientes de {historico.total_pedidos}.
                </p>
              )}
              <p className="part-history-pie">
                El precio por unidad no está en el sistema: lo que hay es el importe facturado del pedido
                y las horas de los partes de trabajo.
              </p>
            </div>
          )}

          {ofertas.length > 0 && (
            <div className="part-history-cuerpo">
              <h3>Ofertas anteriores de esta pieza</h3>
              <ul className="part-history-ofertas">
                {ofertas.slice(0, 5).map(o => (
                  <li key={o.hash}>
                    <button type="button" onClick={() => navigate(`/offers/${o.hash}`)}>
                      {fecha(o.fecha)} · {o.cliente || 'sin cliente'} · {euros(o.importe_estimado)} · {o.estado}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {planos.length > 0 && (
            <div className="part-history-cuerpo">
              <h3>Planos anteriores con este código</h3>
              <ul className="part-history-planos">
                {planos.slice(0, 6).map(p => (
                  <li key={p.analysis_hash}>
                    <button type="button" onClick={() => window.open(`/analysis/${p.analysis_hash}`, '_blank')}>
                      {p.filename}
                    </button>
                    <span>{p.cliente || '—'} · {fecha(p.fecha_documento || p.analyzed_at)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {!hayHistorico && (
            <div className="part-history-cuerpo">
              <p className="part-history-vacio">
                No hay ningún pedido, oferta ni plano anterior con este código. Puede ser una pieza nueva,
                o que el cliente haya cambiado el código.
              </p>
              {similares === null ? (
                <p className="part-history-pie">Mirando si hay planos parecidos…</p>
              ) : similares.length > 0 ? (
                <>
                  <h3>Planos parecidos ya trabajados</h3>
                  <ul className="part-history-similares">
                    {similares.slice(0, 5).map(s => (
                      <li key={s.analysis_hash}>
                        <b>{Math.round((s.score || 0) * 100)}%</b>
                        <button type="button" onClick={() => window.open(`/analysis/${s.analysis_hash}`, '_blank')}>
                          {s.filename}
                        </button>
                        {s.reference && <span>{s.reference}</span>}
                      </li>
                    ))}
                  </ul>
                  <p className="part-history-pie">
                    Esto es un parecido visual, no una coincidencia de código: hay que mirarlo antes de usarlo.
                  </p>
                </>
              ) : (
                <p className="part-history-pie">
                  {sinPlano
                    ? 'No hay plano con el que comparar, así que los parecidos no se han podido mirar.'
                    : 'Este documento todavía no tiene planos parecidos calculados, así que no se ha podido mirar.'}
                </p>
              )}
            </div>
          )}
        </div>
      )}

      <div className="part-history-acciones">
        <button
          type="button"
          className="part-history-secundario"
          onClick={() => (window.history.length > 1 ? navigate(-1) : navigate('/similarity-search'))}
        >
          Atrás
        </button>
        <button
          type="button"
          className="part-history-principal"
          onClick={aLaOferta}
        >
          {hayHistorico ? 'Usar como base y montar la oferta' : 'Ofertar desde cero'}
        </button>
      </div>
    </div>
  );
};

export default PartHistoryPage;
